import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { RpmConstants } from '../constants';
import { IS_SKIP_HANDLE_ERROR } from './api/api-http-context-token';
import { AuthService } from './api/auth.service';
import { IAppConfig } from './config/app-config.model';
import { APP_CONFIG } from './config/app-config.service';
import { ErrorService } from './error.service';
import { LogService } from './log.service';
import { PlatformService } from './platform.service';
import { SentryService } from './sentry.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  errors: any[];
  constructor(
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private platform: PlatformService,
    private errorService: ErrorService, 
    private router: Router,
    private auth: AuthService,
    private messageService: MessageService,
    private translate: TranslateService,
    private log: LogService,
    private sentryService: SentryService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isAuthenticated = this.auth.isLoggedIn();

        return next.handle(request).pipe(
          tap(res => {
            if(res.type === 4 && res.status === 200){
              const body: any = res.body;
              if(body){
                if(!body.success && body.errors && body.errors.length){
                  const errorCode = body.errors[0].code;
                  const desc = body.errors[0].description;

                  this.sentryService.captureMessage(`API Error. User=${this.auth.getUsername()}, Code=${errorCode}, Desc=${desc}, URL=${request.url}`);

                  if(window.location.href.indexOf('localhost') !== -1 && errorCode === 'CRSAPIGateway'){
                    return;
                  }

                  if(request.context?.get(IS_SKIP_HANDLE_ERROR) === true){
                    return
                  }
                  
                  // If error code starts with BS or DS => show code + "common.error.general"
                  // DS0701: Reset password code is expired.
                  // if(/^(BS|DS)/i.test(errorCode) && errorCode !== 'DS0701'){
                  if(!desc){
                    this.translate.get('common.error.general').subscribe(value => {
                      this.messageService.add({severity:'error', summary: `Error ${errorCode}`, detail: value, key: 'toast'});
                    });
                    return;
                  }
                  this.log.log(`API Error ${errorCode}, desc=${desc}`);
                  this.messageService.add({severity:'error', summary: `Error ${errorCode}`, detail: desc, key: 'toast', life: 300000});

                  // Invalid token
                  if(errorCode === 'XWAPI202' && this.config.isUsingExternalIdentityServer){
                    // logout from keycloak
                    setTimeout(() => {
                      this.auth.forceLogoutFromIDP();
                    }, 3000);
                  }
                }
              }
            }
          }),
          tap({
            error: (err) => {
              if(err){
                (err as any).isApiError = true;
              }

              if (window.navigator.onLine) {
                if(request.context?.get(IS_SKIP_HANDLE_ERROR) === true){
                  this.log.log(`ErrorInterceptor IS_SKIP_HANDLE_ERROR: ${JSON.stringify(err)}`);
                  if(err){
                    (err as any).handled = true;
                  }
                  return throwError(() => err);
                }
                // ROP-8501: Show detail error when bad request if error description is in body.
                let errorMsg: string = '';
                if(err && err.error){
                  let errorDetail: any = err.error;
                  if(errorDetail.errors && errorDetail.errors.length){
                    let error = errorDetail.errors[0];
                    if(error.code != error.description){
                      errorMsg = error.description;
                      if(err.status === 400){
                        (err as any).handled = true;
                        this.messageService.add({severity:'error', summary: `Error ${error.code}`, detail: errorMsg, key: 'toast', life: 10000});
                        return throwError(() => err);
                      }
                    }
                  }
                }

                this.log.log(`ErrorInterceptor catchError: ${err?.status}. Error=${err && err.message ? err.message : ''}. errorMsg=${errorMsg}`);
                if ([401, 403, 409].includes(err.status) /*&& isAuthenticated*/) {
                  // auto logout if 401 or 403 response returned from api
                  if (!this.router.url.includes("login")) {
                    if(err.status === 409){
                      this.auth.show409Error();
                    }
                    setTimeout(() => {
                      this.auth.logout(true, null, err && err.status === 409 ? 2000: 0);
                      sessionStorage.clear();
                    }, 4000);
                    return throwError(() => err);
                  }
                }
  
                return throwError(() => err);
              } else {
                // Internet interrupt
                return throwError(() => "12004");
              }
            }
          })
        );
  }
}